import type { InstanceGroupInfo, InstanceInfo } from '@/database/utils/pa/pa.types';
import { type DBType } from '@/common/utils';
import { getDbInstanceMeta } from '@/api-client/database/queries/database-cmmn-controller/database-cmmn-controller';
import { getV8DatabaseInstanceTargetCheckByInstanceId } from '@/api-client/metav8Over/queries/meta-database-instance-v8-controller/meta-database-instance-v8-controller';
import type { DbInstanceMetaResponse } from '@/api-client/database/model';
import type { InstanceV9ItemType } from '@/api-client/metav8Over/model';

const getInstanceItem = (groupItem: DbInstanceMetaResponse, useFolder?: boolean) => {
  return (groupItem.instances || [])
    .filter(({ instanceName, instanceId }) => instanceName && instanceId)
    .map<InstanceInfo>((instanceItem) => {
      return {
        instanceId: instanceItem.instanceId!,
        name: instanceItem.instanceName!,
        alias: instanceItem.alias,
        enabled: instanceItem.enabled,
        type: instanceItem.dbType as InstanceV9ItemType,
        instanceState: {
          version: instanceItem.dbVersion,
        },
        folders: useFolder
          ? [{ folderId: groupItem.groupId, name: groupItem.groupName }]
          : undefined,
        singlestoreClusterId: instanceItem.singlestoreClusterId,
      };
    });
};

const convertDbInstanceMetaToFolderItems = (
  data: DbInstanceMetaResponse[],
): InstanceGroupInfo[] => {
  return data.map((groupItem) => {
    const folderInstanceItem: InstanceGroupInfo = {
      folderId: groupItem.groupId,
      name: groupItem.groupName,
      instances: getInstanceItem(groupItem, false),
    };
    return folderInstanceItem;
  });
};

const convertDbInstanceMetaToInstances = (data: DbInstanceMetaResponse[]): InstanceInfo[] => {
  const allInstances: InstanceInfo[] = [];

  data.forEach((groupItem) => {
    const instances = getInstanceItem(groupItem, true);
    allInstances.push(...instances);
  });

  return allInstances;
};

const fetchInstanceGroupsByDbType = async (
  dbType?: NonNullable<DBType>,
  signal?: AbortController['signal'],
): Promise<DbInstanceMetaResponse[]> => {
  try {
    const {
      data: { data = [] },
    } = await getDbInstanceMeta(
      { dbmsType: dbType },
      { params: { isTimeout: false, isPolling: true } },
      signal,
    );
    return data;
  } catch (error: unknown) {
    console.error(error);
    return [];
  }
};

export const getInstancesInfo = async ({
  dbType,
  signal,
  dataType,
}: {
  dbType?: NonNullable<DBType>;
  signal?: AbortController['signal'];
  dataType: 'group' | 'instance' | 'all';
}) => {
  const returnData: { folders: InstanceGroupInfo[]; instances: InstanceInfo[] } = {
    folders: [],
    instances: [],
  };
  const data = await fetchInstanceGroupsByDbType(dbType, signal);

  if (dataType === 'group' || dataType === 'all') {
    returnData.folders = convertDbInstanceMetaToFolderItems(data);
  }
  if (dataType === 'instance' || dataType === 'all') {
    returnData.instances = convertDbInstanceMetaToInstances(data);
  }

  return returnData;
};

export const fetchInstanceTargetCheck = async (instanceId: string) => {
  try {
    const {
      data: { data = {} },
    } = await getV8DatabaseInstanceTargetCheckByInstanceId(instanceId);
    return data;
  } catch (error: unknown) {
    console.error(error);
    return null;
  }
};
